// This file will hold all your service content.
// "slug" is the unique URL part (e.g., /services/anxiety-stress)
// "title" is for the card and the page heading
// "summary" is the short text for the card on the homepage
// "content" is the detailed information for the service's own page

export const serviceData = [
  // --- YOUR TWO NEW SERVICES ---
  {
    slug: 'counselling-psychotherapy',
    title: 'Counselling & Psychotherapy',
    summary: 'A generic overview of what I can offer for all people, providing a safe, non-judgmental space to explore your thoughts and feelings.',
    content: (
      <>
        <p>Counselling and psychotherapy offer a confidential and supportive space for you to talk about issues and concerns that you are facing in your life. As a BACP accredited therapist, I am here to listen without judgment and to help you find new ways to approach your challenges.</p>
        
        <h3>How It Works</h3>
        <p>Our sessions are a collaborative process. We'll work together to understand your unique situation. Whether you're feeling stuck, facing a difficult decision, or simply want to understand yourself better, therapy can provide the clarity you need.</p>
        
        <h3>Typical Sessions & Approach</h3>
        <p>A typical session lasts 50 minutes, usually on a weekly basis. The total number of sessions depends entirely on your needs and goals. My approach is integrative, meaning I draw from various therapeutic models to find what works best for you.</p>
      </>
    )
  },
  {
    slug: 'relationship-counselling',
    title: 'Relationship Counselling',
    summary: 'Specialist counselling for couples. We will use methods from leading experts like the Gottman Institute to improve communication and rebuild connection.',
    content: (
      <>
        <p>Relationships can be one of our greatest sources of joy, but also our greatest challenge. Whether you're a couple feeling stuck in a cycle of conflict, or you're struggling to communicate, there is a path forward.</p>
        
        <h3>My Approach: The Gottman Method & More</h3>
        <p>My work is heavily influenced by the world-renowned Gottman Institute, which uses decades of research to understand what makes relationships work. We'll focus on practical, evidence-based tools to:</p>
        <ul>
          <li>Disarm conflicting verbal communication</li>
          <li>Increase intimacy, respect, and affection</li>
          <li>Remove barriers that create a feeling of stagnancy</li>
          <li>Create a heightened sense of empathy and understanding</li>
        </ul>

        <h3>How It Works</h3>
        <p>We'll start by assessing your relationship's strengths and areas for improvement. From there, we'll build a tailored plan. This isn't about blaming, but about understanding patterns and building a stronger, more resilient partnership.</p>
      </>
    )
  },
  
  // --- YOUR THREE EXISTING SERVICES ---
  {
    slug: 'anxiety-stress',
    title: 'Anxiety & Stress Management',
    summary: 'For the individual feeling pulled in a million directions. Learn practical tools to navigate pressure with serenity and find a healthier work-life balance.',
    content: (
      <>
        <p>Anxiety can manifest in many ways. Sometimes it’s a specific, looping worry. Other times, it’s a constant, underlying hum of stress you can't seem to shake. Whether you're a professional juggling deadlines or navigating complex family dynamics, this state is exhausting.</p>
        <p>My goal is to help you move beyond this. There isn't just one way to tackle anxiety; it’s about finding the <strong>right combination of tools for you</strong>. My eclectic approach is designed to do just that.</p>
        
        <h3>Part 1: Changing Your Thoughts (CBT)</h3>
        <p>A significant part of anxiety is often fueled by "distorted thoughts" — a mental filter that magnifies the negative and discounts the positive. Using techniques from <strong>Cognitive Behavioural Therapy (CBT)</strong>, we can actively work to solve this. We will learn to:</p>
        <ul>
          <li><strong>Identify:</strong> Become aware of these automatic negative thoughts as they happen. We'll catch the "what-if" cycles and the "all-or-nothing" thinking.</li>
          <li><strong>Challenge:</strong> Gently question these thoughts. Are they 100% true? What's the evidence? What is a more balanced, realistic way to see this?</li>
          <li><strong>Reframe:</strong> Actively replace these distorted thoughts with more helpful and accurate ones, reducing their power and emotional impact.</li>
        </ul>
        
        <h3>Part 2: Changing Your Relationship with Anxiety (Acceptance)</h3>
        <p>But what about the anxiety that just... *is*? For general, persistent anxiety, sometimes the *struggle itself* is the problem. Fighting the feeling only seems to make it stronger.</p>
        <p>Here, we will use powerful <strong>Acceptance-based techniques</strong> (from models like Acceptance and Commitment Therapy, or ACT). This approach isn't about *eliminating* anxiety, but about *changing your relationship with it*. We'll work on:</p>
        <ul>
          <li><strong>Acceptance:</strong> Learning to make space for anxious feelings without struggling against them or letting them define you.</li>
          <li><strong>Defusion:</strong> Seeing your thoughts as just "thoughts"—passing mental events—rather than absolute truths that must control your actions.</li>
          <li><strong>Values-Based Action:</strong> Identifying what truly matters to you (your values) and taking committed steps toward them, *even with anxiety present*.</li>
        </ul>

        <h3>How This Eclectic Approach Brings You 'Rida'</h3>
        <p>This is how we find your <strong>Rida (contentment)</strong>. An eclectic approach gives us two powerful paths: CBT helps you solve the "solvable" parts of your anxiety—the distorted thoughts you can actively change. Acceptance techniques help you find peace with the "unsolvable" parts—the general feelings you can learn to live with, without letting them stop you from building a meaningful life.</p>
        <p>By blending these, you build a complete toolkit. You learn when to challenge your mind and when to accept it, allowing you to regain control and find a lasting sense of inner calm.</p>

        <h3>Sessions & Duration</h3>
        <p>We will typically meet for 50 minutes once a week. The total duration varies for everyone. Some clients find new, effective tools within <strong>8-12 sessions</strong>. For others, this work is part of a longer-term journey. We will regularly review our progress to ensure our sessions are always focused on your goals.</p>
      </>
    )
  },
  {
    slug: 'cultural-identity',
    title: 'Counselling for Cultural Identity',
    summary: 'For the individual navigating the delicate balance of two worlds, family expectations, and generational trauma. A safe space to find your own voice.',
    content: (
      <>
        <p>For the individual navigating the delicate balance of two worlds. If you're struggling with the weight of family expectations, feeling invalidated, or processing generational trauma, this is a safe space to find your own voice, build your self-worth, and set boundaries without guilt.</p>
        
        <h3>How It Works</h3>
        <p>(Add more detail here about your approach to these specific issues...)</p>
      </>
    )
  },
  {
    slug: 'relationship-family-conflict',
    title: 'Relationship & Family Counselling',
    summary: 'For couples and individuals facing cultural and family conflicts, such as over-involved in-laws and differing values. Learn to communicate and work as a team.',
    content: (
      <>
        <p>For the couple facing cultural and family conflicts. We can address the challenges of over-involved in-laws and differing cultural values. Learn to communicate better, support each other, and work as a team to build a relationship that honours both your partnership and your families.</p>
        
        <h3>How It Works</h3>
        <p>(Add more detail here... This can focus more on the family/cultural conflict aspect, while the other "Relationship Counselling" service is more for the couple's internal dynamic.)</p>
      </>
    )
  },
];

export default serviceData;
